// Reusable authenticated read for the extensible boat-information aggregate
// (api_contracts §5.3). Updates are invalidated by the already-open user
// WebSocket rather than a per-mount polling timer.
//
// The navigation section is the BOAT'S answer, mirrored (cloud ADR 0188 / core
// ADR-142). The cloud no longer classifies anything, so nothing here may
// improve on what the boat said — including by guessing a layout when the boat
// has not answered.

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  modeToOverviewArchetype,
  type ModeStateCandidate,
  type OverviewArchetype,
} from '@neomaris/protocol';

import { api } from '../api';
import { useAuth } from '../auth';

export interface BoatStateResponse {
  status: 'ready' | 'stale' | 'insufficient_data';
  /** The three-view fold of the boat's mode. `null` = we cannot tell. */
  state: OverviewArchetype | null;
  confidence: string;
  reason: string;
  observedAt: string | null;
  computedAt: string | null;
  /** How long the cloud goes without hearing from the BOAT before it stops
   *  calling the mirrored answer current. Not a snapshot lifetime. */
  staleAfterSeconds: number;
  evidence: Record<string, unknown>;
  // Everything below is what the boat actually said. `null` throughout when no
  // snapshot has ever arrived — an old core, or a boat the cloud has not met.
  mode: string | null;
  modeSince: string | null;
  /** `classifier`, `manual`, or the `boot` seed sentinel. */
  detectedBy: string | null;
  primaryMode: string | null;
  lastKnownMode: string | null;
  modeStatus: string | null;
  reasonCode: string | null;
  /**
   * The boat held this answer over instead of measuring it. Optional so a
   * client running against an older cloud still parses; absent reads as false,
   * which is what every response predating the field meant.
   */
  heldOver?: boolean;
  /** When the answer was last MEASURED, as opposed to last reported. */
  measuredAt?: string | null;
  candidates: readonly ModeStateCandidate[];
  classifierVersion: number | null;
  authorityEpoch: number | null;
  revision: number | null;
  receivedAt: string | null;
  /** When the cloud last heard from the boat at all. THE freshness input. */
  boatLastSeenAt: string | null;
}

export interface BoatInformationResponse {
  schemaVersion: 1;
  boat: {
    id: string;
    name: string;
    displayName: string;
    nameSource: 'ais' | 'configured';
    type: {
      propulsion: 'sail' | 'motor' | null;
      source: 'ais' | null;
      aisShipType: { code: number; label: string } | null;
    };
    identifiers: { mmsi: number | null; imo: number | null; callsign: string | null };
    dimensions: {
      lengthMeters: number | null;
      beamMeters: number | null;
      toBowMeters: number | null;
      toSternMeters: number | null;
      toPortMeters: number | null;
      toStarboardMeters: number | null;
    };
    identity: {
      aisName: string | null;
      aisIdentitySeenAt: string | null;
      aisLastSeenAt: string | null;
    };
  };
  navigation: { state: BoatStateResponse };
}

export type BoatInformationLoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; information: BoatInformationResponse }
  | { kind: 'error'; message: string };

export interface UseBoatInformation {
  state: BoatInformationLoadState;
  refresh: () => Promise<void>;
}

/**
 * What the boat reported, in the three shapes a screen has to tell apart.
 *
 * `reported` carries a nullable `archetype` on purpose: a mode this client has
 * no layout for is still a mode the boat measured and the chip may name it,
 * while the LAYOUT falls back to nothing rather than to a guess.
 */
export type ReportedMode =
  | {
      kind: 'reported';
      archetype: OverviewArchetype | null;
      mode: string;
      detectedBy: string;
      modeStatus: string;
      confidence: string;
      reasonCode: string;
      /**
       * The answer is a memory, not a measurement. `because` describes the
       * window it was measured in, which is no longer the current one.
       */
      heldOver: boolean;
      /** When it was last measured. Drives "held from N ago". */
      measuredAt: string | null;
      /** The strongest candidate's boater-facing sentence. May be empty. */
      because: string;
      /**
       * The same sentence as a code plus its measurements, so the chip can say
       * it in THIS account's units. Null from a core too old to send one — the
       * prose above is what that boat has, and it is still what ships.
       */
      becauseCode: string | null;
      becauseParams: Record<string, { value: number; unit: string }> | null;
      since: string | null;
      /** Display copy only. Never an input to freshness — see below. */
      computedAt: string | null;
    }
  | {
      kind: 'stale';
      /** The label the boat last stood behind, for "last reported Anchored". */
      lastKnownMode: string | null;
      boatLastSeenAt: string | null;
    }
  | { kind: 'unknown'; reason: string };

/** No snapshot has ever arrived: an old core, or a boat we have not met. */
const NO_REPORT = 'no_report';
/** The boat has a seed value it has not measured anything to support. */
const BOOT_SEED = 'boot_seed';

/**
 * Read the boat's mode answer.
 *
 * Trusted ONLY when a snapshot exists, was not the `boot` seed, and the boat
 * did not itself say `unknown`. There is no fallback layout: a mode nobody
 * measured, rendered as Underway, is the stuck-Underway failure this whole
 * slice exists to end.
 *
 * FRESHNESS IS A QUESTION ABOUT THE LINK, NOT ABOUT THE SNAPSHOT. A boat at
 * anchor reports the same answer for a week and is perfectly healthy, so
 * snapshot age says nothing about whether the answer is still true — silence on
 * the wire says exactly that. Only `boatLastSeenAt` is read here.
 */
export function resolveReportedMode(
  information: BoatInformationResponse | null,
  now = Date.now()
): ReportedMode {
  // Optional all the way down. `navigation` is contractually always present,
  // but reading it unguarded throws on a payload that lacks it, and a throw
  // here takes the WHOLE dashboard down rather than one chip — the failure is
  // total and looks nothing like its cause. Absent reads as "no answer", which
  // is both true and survivable.
  const snapshot = information?.navigation?.state;
  if (snapshot === undefined || snapshot.detectedBy === null || snapshot.receivedAt === null) {
    return { kind: 'unknown', reason: NO_REPORT };
  }
  if (snapshot.detectedBy === 'boot') {
    return { kind: 'unknown', reason: BOOT_SEED };
  }
  // Unknown beats stale: an answer the boat never had cannot be out of date.
  // A hand-pinned mode arrives as `manual`, never `unknown` — the boat resolves
  // that (core ADR-142) so this reader needs no exception for it.
  if (snapshot.modeStatus === 'unknown') {
    return { kind: 'unknown', reason: snapshot.reasonCode ?? NO_REPORT };
  }
  const mode = snapshot.mode;
  if (mode === null) {
    return { kind: 'unknown', reason: snapshot.reasonCode ?? NO_REPORT };
  }
  if (snapshot.status === 'stale' || outOfContact(snapshot, now)) {
    return {
      kind: 'stale',
      lastKnownMode: mode,
      boatLastSeenAt: snapshot.boatLastSeenAt,
    };
  }
  return {
    kind: 'reported',
    archetype: modeToOverviewArchetype(mode),
    mode,
    detectedBy: snapshot.detectedBy,
    modeStatus: snapshot.modeStatus ?? '',
    confidence: snapshot.confidence,
    reasonCode: snapshot.reasonCode ?? '',
    heldOver: snapshot.heldOver ?? false,
    measuredAt: snapshot.measuredAt ?? null,
    because: snapshot.candidates[0]?.because ?? '',
    becauseCode: becauseCodeFrom(snapshot.candidates[0]),
    becauseParams: becauseParamsFrom(snapshot.candidates[0]),
    since: snapshot.modeSince,
    computedAt: snapshot.computedAt,
  };
}

/**
 * Read one of the structured-reason fields off a candidate as it ARRIVED.
 *
 * Taken as `unknown` and narrowed here rather than read straight off
 * `ModeStateCandidate`, which describes what a CURRENT core sends, not what
 * this payload holds. Both fields are optional — an older core sends neither —
 * and the mirror's read side re-casts its stored jsonb without revalidating it,
 * so a row written before these fields existed reaches this reader unchecked.
 */
function wireField(candidate: unknown, key: 'becauseCode' | 'becauseParams'): unknown {
  return typeof candidate === 'object' && candidate !== null
    ? (candidate as Record<string, unknown>)[key]
    : undefined;
}

function becauseCodeFrom(candidate: unknown): string | null {
  const code = wireField(candidate, 'becauseCode');
  return typeof code === 'string' && code !== '' ? code : null;
}

/**
 * The measurements behind the code, validated one by one.
 *
 * ONE bad parameter nulls the whole set. A sentence is filled from these, and a
 * half-filled sentence ("Swinging in a  circle") reads as a broken product,
 * while dropping to the boat's own prose reads as normal.
 */
function becauseParamsFrom(
  candidate: unknown
): Record<string, { value: number; unit: string }> | null {
  const raw = wireField(candidate, 'becauseParams');
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const entries: [string, { value: number; unit: string }][] = [];
  for (const [key, entry] of Object.entries(raw)) {
    if (typeof entry !== 'object' || entry === null) return null;
    const { value, unit } = entry as { value?: unknown; unit?: unknown };
    if (typeof value !== 'number' || !Number.isFinite(value)) return null;
    if (typeof unit !== 'string' || unit === '') return null;
    entries.push([key, { value, unit }]);
  }
  // `fromEntries` defines own properties rather than assigning them, so a
  // `__proto__` key in the frame cannot reach an object's prototype setter.
  return Object.fromEntries(entries);
}

function outOfContact(snapshot: BoatStateResponse, now: number): boolean {
  const seenAt = snapshot.boatLastSeenAt === null ? NaN : Date.parse(snapshot.boatLastSeenAt);
  if (!Number.isFinite(seenAt)) return true;
  const window = staleWindowMs(snapshot);
  if (window === null) return true;
  return now - seenAt > window;
}

/** The server declares the window. A missing or nonsense one reads as out of
 *  contact rather than as "fresh forever" — the same fail-safe direction, and
 *  without a second copy of the server's number to drift from. */
function staleWindowMs(snapshot: BoatStateResponse): number | null {
  const seconds = snapshot.staleAfterSeconds;
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : null;
}

function informationErrorMessage(error: unknown): string {
  const failure = error as { status?: number; message?: string; body?: { message?: string } };
  if (failure.status === 401)
    return 'Your session expired. Sign in again to see vessel information.';
  return (
    failure.body?.message ?? failure.message ?? 'Vessel information is temporarily unavailable.'
  );
}

/**
 * Full load state for UI surfaces that must distinguish loading and failure
 * from an aggregate whose fields are genuinely absent.
 */
export function useBoatInformation(boatId: string, infoRevision = 0): UseBoatInformation {
  const auth = useAuth();
  const [state, setState] = useState<BoatInformationLoadState>({ kind: 'loading' });
  const mountedRef = useRef(true);
  const requestRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const request = ++requestRef.current;
    if (boatId === '') {
      if (mountedRef.current) setState({ kind: 'loading' });
      return;
    }
    try {
      const information = await api<BoatInformationResponse>(`/v1/boats/${boatId}/info`);
      if (mountedRef.current && request === requestRef.current) {
        // Everything below reads `boat` and `navigation.state` without checks,
        // and the boat shell reads this on every boat page, so an answer
        // without them is an error here rather than a crash there.
        setState(
          information?.boat && information.navigation?.state
            ? { kind: 'ready', information }
            : { kind: 'error', message: 'The boat information was incomplete.' }
        );
      }
    } catch (error) {
      const failure = error as { status?: number };
      if (failure.status === 401) {
        await auth.refresh();
      }
      if (mountedRef.current && request === requestRef.current) {
        // A transient refresh failure should not erase a previously rendered
        // aggregate. Initial failures remain explicit and retryable.
        setState((current) =>
          current.kind === 'ready'
            ? current
            : { kind: 'error', message: informationErrorMessage(error) }
        );
      }
    }
  }, [auth, boatId]);

  useEffect(() => {
    setState((current) =>
      current.kind === 'ready' && current.information.boat.id === boatId
        ? current
        : { kind: 'loading' }
    );
    void load();
  }, [boatId, infoRevision, load]);

  // Staleness is time-driven, so it does not naturally produce a WS event.
  // Schedule one local transition at the server-declared boundary; this is a
  // one-shot render correction, not a polling loop or a second network read.
  //
  // Keyed on when the cloud last heard from the BOAT, the same fact the server
  // keys on. Keyed on snapshot age instead, a boat that has sat at anchor for a
  // week — reporting the same answer, perfectly healthy — goes stale on screen.
  useEffect(() => {
    const information = state.kind === 'ready' ? state.information : null;
    const navigationState = information?.navigation.state;
    if (navigationState?.status !== 'ready' || navigationState.boatLastSeenAt === null) return;
    const lastSeenAt = Date.parse(navigationState.boatLastSeenAt);
    if (!Number.isFinite(lastSeenAt)) return;
    const delay = Math.max(0, lastSeenAt + navigationState.staleAfterSeconds * 1000 - Date.now());
    const timer = window.setTimeout(() => {
      setState((current) => {
        if (current.kind !== 'ready') return current;
        const currentState = current.information.navigation.state;
        if (
          currentState.status !== 'ready' ||
          currentState.boatLastSeenAt !== navigationState.boatLastSeenAt
        ) {
          return current;
        }
        return {
          kind: 'ready',
          information: {
            ...current.information,
            navigation: {
              ...current.information.navigation,
              state: { ...currentState, status: 'stale' },
            },
          },
        };
      });
    }, delay);
    return () => window.clearTimeout(timer);
  }, [state]);

  const refresh = useCallback(async () => {
    setState((current) => (current.kind === 'error' ? { kind: 'loading' } : current));
    await load();
  }, [load]);

  return { state, refresh };
}

/**
 * Compatibility view for consumers that only need a last-good aggregate.
 * New UI should prefer useBoatInformation so absence is not confused with a
 * request still loading or failing.
 */
export function useBoatInfo(boatId: string, infoRevision = 0): BoatInformationResponse | null {
  const result = useBoatInformation(boatId, infoRevision);
  const information = result.state.kind === 'ready' ? result.state.information : null;

  // Never show boat A's cached aggregate while boat B's request is loading.
  // `boat` is optional-chained too: a malformed or mocked answer without one
  // must read as "no information", not crash every boat page (the boat shell
  // reads this on all of them).
  return information?.boat?.id === boatId ? information : null;
}
